/**
 * 상담기록지 자동 초안 (MASTER §13, DOCUMENT_OUTPUTS §2, Amendment A3).
 * 같은 정보를 두 번 입력하지 않도록, 이미 저장된 구조화 자료를 장신 상담기록지 항목에 옮긴다.
 * 자동으로 채운 항목은 fieldSources = 'auto'로 표시하고, 상담자가 고치면 'user'가 된다.
 */
import type {
  Client,
  CounselingGoal,
  CounselingJournal,
  CounselorMemo,
  IntakeSummary,
  NextSessionBrief,
  PsychologicalAssessment,
  Session,
  SessionAnalysis,
} from '../schema/v1';
import type { JOURNAL_FORM_FIELDS } from '../schema/v1';

export type JournalFormField = (typeof JOURNAL_FORM_FIELDS)[number];

export const JOURNAL_FIELD_LABELS: Record<JournalFormField, string> = {
  counselingReasonAndGoal: '상담 계기/목표',
  assessment: '검사',
  pastCounselingExperience: '과거 상담경험',
  importantIssue: 'Important issue',
  previousSessionIssueOrTask: '전 회기 issue/과제',
  previousTaskCompletion: '과제 수행 여부',
  counselingContent: '상담 내용',
  nextSessionPlan: '다음 회기 계획',
  remarks: '비고',
};

export type JournalAutofillInput = {
  client: Client;
  intake?: IntakeSummary;
  session: Session;
  analysis?: SessionAnalysis;
  goals: CounselingGoal[];
  assessments: PsychologicalAssessment[];
  previousBrief?: NextSessionBrief;
  memos: CounselorMemo[];
};

const join = (xs: (string | undefined | null)[], sep = '\n') => {
  const v = xs.filter((x): x is string => !!x && !!x.trim());
  return v.length ? v.join(sep) : undefined;
};
const bullets = (xs?: string[]) => (xs?.length ? xs.map((x) => `- ${x}`).join('\n') : undefined);

export function autofillJournal(input: JournalAutofillInput): {
  fields: Partial<Record<JournalFormField, string>>;
  fieldSources: Record<string, 'auto'>;
} {
  const { client, intake, session, analysis, goals, assessments, previousBrief } = input;
  const activeGoals = goals.filter((g) => !g.deletedAt && !['achieved', 'terminated'].includes(g.status));

  const fields: Partial<Record<JournalFormField, string>> = {
    counselingReasonAndGoal: join([
      intake?.referralReason && `상담 계기: ${intake.referralReason}`,
      intake?.presentingProblems.length ? `주호소: ${intake.presentingProblems.join(', ')}` : undefined,
      activeGoals.length ? `상담 목표:\n${activeGoals.map((g) => `- ${g.title}`).join('\n')}` : undefined,
    ]),
    assessment: join(
      assessments
        .filter((a) => !a.deletedAt)
        .map((a) => `${a.testName}${a.scoresSummary ? `: ${a.scoresSummary}` : ''}`),
    ),
    pastCounselingExperience: client.priorCounselingHistory,
    importantIssue: join([
      session.primaryThemes?.length ? session.primaryThemes.join(', ') : undefined,
      session.sessionFocus,
    ]),
    previousSessionIssueOrTask: previousBrief
      ? join([bullets(previousBrief.sessionObjectives), bullets(previousBrief.unfinishedTopics)])
      : undefined,
    counselingContent: join([analysis?.sessionSummary, bullets(analysis?.keyEvents)]),
  };

  const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined)) as Partial<
    Record<JournalFormField, string>
  >;
  return { fields: clean, fieldSources: Object.fromEntries(Object.keys(clean).map((k) => [k, 'auto' as const])) };
}

/** 사용자가 고친 항목은 'user'로 표시 */
export function markUserEdits(
  journal: Pick<CounselingJournal, 'fieldSources'>,
  edited: string[],
): Record<string, 'auto' | 'ai-draft' | 'user'> {
  return { ...(journal.fieldSources ?? {}), ...Object.fromEntries(edited.map((k) => [k, 'user' as const])) };
}
